import React from "react";
import { Box, Typography } from "@mui/joy";
import { useLocation, useNavigate } from "react-router-dom";
import type { CanvasDataSource } from "../../../types";
import type { RenderableElement } from "../CanvasRenderer";
import { useCanvasRuntime } from "../CanvasRuntimeContext";
import { useRelatedRecords } from "../../../hooks/useRelatedRecords";
import {
  useViewQuery,
  useCreateRowMutation,
  useUpdateRowMutation,
} from "../../../hooks/useViewDataQuery";
import { ViewGridNew } from "../../viewGrid";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import { openCanvasNavigation } from "../canvasNavigation";

export interface RelatedRecordsConfig {
  related_data_source_alias?: string;
  relationship_path?: string;
  display_fields?: string[];
  max_records?: number;
  allow_create?: boolean;
  allow_edit?: boolean;
}
interface Props {
  element: RenderableElement;
  dataSources: CanvasDataSource[];
  canvasId?: string | null;
}
const matches = (source: CanvasDataSource, alias: string) =>
  [source.id, source.name, (source as { alias?: string }).alias].includes(
    alias,
  );

/** Uses the server's selected-record relationship query; never queries all target rows. */
export function CanvasRelatedRecordsRenderer({
  element,
  dataSources,
  canvasId,
}: Props) {
  const outer = element.props as unknown as {
    title?: string;
    data_source_alias?: string;
    related_records_config?: RelatedRecordsConfig;
    row_click_action?: { type: string; config?: Record<string, unknown> };
  };
  const config = outer.related_records_config || {};
  const runtime = useCanvasRuntime();
  const effectiveCanvasId = canvasId ?? runtime?.canvasId ?? null;
  const alias = outer.data_source_alias || "";
  const source = dataSources.find((candidate) => matches(candidate, alias));
  const related = dataSources.find((candidate) =>
    matches(candidate, config.related_data_source_alias || ""),
  );
  const primary =
    dataSources.find((candidate) => candidate.is_primary) || dataSources[0];
  const selected = runtime?.interactionBus.getSelectedRow({
    canvasId: effectiveCanvasId,
    dataSourceAlias: alias,
  });
  // The bus can return a sole selection from another alias. Related rows cannot use that fallback.
  const selectedMatches =
    selected &&
    source &&
    (selected.dataSourceAlias
      ? matches(source, selected.dataSourceAlias)
      : selected.viewId === String(source.view_id));
  const primaryRowId =
    runtime?.primaryRowId === undefined ? runtime?.rowId : runtime.primaryRowId;
  const rowId = selectedMatches
    ? selected.rowId
    : source === primary
      ? (primaryRowId ?? null)
      : null;
  const configured = Boolean(
    source?.view_id &&
    related?.view_id &&
    config.relationship_path &&
    config.display_fields?.length,
  );
  const query = useRelatedRecords(
    configured ? effectiveCanvasId : null,
    element.id,
    configured ? rowId : null,
  );
  const viewId = related?.view_id ? String(related.view_id) : null;
  const view = useViewQuery(configured ? viewId : null);
  const create = useCreateRowMutation();
  const update = useUpdateRowMutation();
  const context = effectiveCanvasId
    ? {
        canvasId: effectiveCanvasId,
        elementId: element.id,
        embeddingPath: runtime?.embeddingPath,
      }
    : undefined;
  const writable = element.effectiveAccess !== "read";
  const allowEdit = writable && config.allow_edit === true;
  const allowCreate = writable && config.allow_create === true;
  const authFetch = useAuthFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const [navigationError, setNavigationError] = React.useState<string | null>(
    null,
  );
  const error = query.error || view.error;
  const message = !configured
    ? "Configure the source, related View, relationship and display fields."
    : !rowId
      ? "Select a record to see its related records."
      : error
        ? error instanceof Error
          ? error.message
          : "Unable to load related records."
        : null;
  if (message)
    return (
      <Typography role={error ? "alert" : undefined}>{message}</Typography>
    );
  return (
    <Box sx={{ minHeight: 260 }}>
      <Typography level="title-sm">
        {outer.title || "Related records"}
      </Typography>
      {navigationError && (
        <Typography role="alert">{navigationError}</Typography>
      )}
      {query.isLoading || view.isLoading ? (
        <Typography>Loading related records…</Typography>
      ) : view.data && query.data ? (
        <>
          <Typography level="body-sm">
            Page {query.page} · {query.data.totalCount} related records
          </Typography>
          <ViewGridNew
            key={JSON.stringify([
              runtime?.workspaceId,
              effectiveCanvasId,
              element.id,
              rowId,
            ])}
            view={view.data}
            data={query.data}
            loading={query.isFetching || create.isPending || update.isPending}
            page={query.page - 1}
            pageSize={query.data.pageSize}
            onPageChange={(page) => query.setPage(page + 1)}
            onPageSizeChange={() => {}}
            visibleColumns={config.display_fields}
            onRowCreate={
              allowCreate
                ? async (data) => {
                    await create.mutateAsync({
                      viewId: viewId!,
                      data,
                      context,
                    });
                    await query.refetch();
                  }
                : undefined
            }
            onRowUpdate={
              allowEdit
                ? async (id, data) => {
                    await update.mutateAsync({
                      viewId: viewId!,
                      rowId: id,
                      data,
                      context,
                    });
                    await query.refetch();
                  }
                : undefined
            }
            onRowSelect={
              outer.row_click_action?.type === "NAVIGATE"
                ? async (id) => {
                    if (!runtime) return;
                    setNavigationError(null);
                    try {
                      await openCanvasNavigation(
                        outer.row_click_action?.config || {},
                        {
                          workspaceId: runtime.workspaceId,
                          currentRowId: rowId,
                          selectedRowId: id,
                          returnPath: location.pathname + location.search,
                          authFetch,
                          navigate,
                        },
                      );
                    } catch (err) {
                      setNavigationError(
                        err instanceof Error
                          ? err.message
                          : "Destination unavailable.",
                      );
                    }
                  }
                : undefined
            }
            onRefresh={() => {
              void query.refetch();
            }}
            showToolbar={allowCreate}
            showGridToolbar={false}
            height={Math.max(element.rowSpan * 60 - 56, 260)}
            writeContext={context}
            allowCreate={allowCreate}
            allowEdit={allowEdit}
            allowDelete={false}
            allowBulkActions={false}
            allowImport={false}
            allowAuditLog={false}
          />
          {query.data.rows.length === 0 && (
            <Typography>No related records.</Typography>
          )}
        </>
      ) : null}
    </Box>
  );
}
